import { createContext, useContext, useEffect, useState } from 'react';
import type { GameEnvelope, MemberView, RoomView } from '@gmp/shared-types/browser';
import type { SoundManager } from './sound';

export interface ActResult {
  ok: boolean;
  error?: string;
}

/** Everything a game's UI needs from the platform. Games never talk to the network directly. */
export interface PartyUi {
  now(): number;
  me: string | null;
  room: RoomView;
  member(id: string | null | undefined): MemberView | undefined;
  act(action: Record<string, unknown>): Promise<ActResult>;
  /** Real-time control input (stick, held buttons). Not acknowledged; send on change, at most ~20/s. */
  input(input: Record<string, unknown>): void;
  upload(blob: Blob, kind: 'image' | 'video', onProgress?: (f: number) => void): Promise<{ mediaId: string } | { error: string }>;
  report(submissionId: string, reason: string): void;
  /** TV / audience screen: large type, no private controls. */
  display: boolean;
  sound: SoundManager;
  reducedMotion: boolean;
}

export const PartyUiContext = createContext<PartyUi | null>(null);

export function usePartyUi(): PartyUi {
  const v = useContext(PartyUiContext);
  if (!v) throw new Error('usePartyUi outside provider');
  return v;
}

export interface GameScreenProps<V> {
  game: GameEnvelope;
  view: V;
}

/** Milliseconds left until a server deadline, ticking on animation frames (or 4x/s when reduced). */
export function useRemaining(endsAt: number | null, now: () => number, hz = 10): number {
  const [left, setLeft] = useState(() => (endsAt === null ? 0 : Math.max(0, endsAt - now())));
  useEffect(() => {
    if (endsAt === null) return setLeft(0);
    const tick = () => setLeft(Math.max(0, endsAt - now()));
    tick();
    const id = window.setInterval(tick, 1000 / hz);
    return () => clearInterval(id);
  }, [endsAt, now, hz]);
  return left;
}
